import { useMemo, useRef, useState, type KeyboardEvent } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ClickAwayListener,
  Divider,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  adminPages,
  matchingAdminPages,
  type AdminPage,
} from "./adminPages.js";

/**
 * One field that both searches and navigates the administrative pages.
 *
 * A menu would do for two pages; this exists because the set is expected to grow
 * to dozens, at which point a menu is a scroll and a search field is a keystroke.
 * Typing filters on label and keywords, the arrow keys move, and Enter goes —
 * so the common case is "type three letters and press Enter" without the mouse.
 */
export const AdminPageSearch = ({ current }: { current?: AdminPage }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const anchor = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(0);

  const groups = useMemo(() => matchingAdminPages(search, t), [search, t]);

  // Flattened alongside the grouped view so the arrow keys move through what is
  // on screen in the order it is drawn, rather than through the groups.
  const visible = useMemo(
    () => groups.flatMap((group) => group.pages),
    [groups],
  );

  const close = () => {
    setOpen(false);
    setSearch("");
    setHighlighted(0);
  };

  const go = (page: AdminPage) => {
    close();
    input.current?.blur();
    navigate(page.path);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((at) => (at + 1) % Math.max(visible.length, 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted(
        (at) =>
          (at - 1 + Math.max(visible.length, 1)) % Math.max(visible.length, 1),
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      // Falls back to the first match, so pressing Enter straight after typing
      // goes where the list is already pointing without an arrow key first.
      const chosen = visible[highlighted] ?? visible[0];
      if (chosen !== undefined) {
        go(chosen);
      }
      return;
    }

    if (event.key === "Escape") {
      close();
      input.current?.blur();
    }
  };

  return (
    <ClickAwayListener onClickAway={close}>
      <Box ref={anchor} sx={{ position: "relative", flex: 1, maxWidth: 420 }}>
        <TextField
          inputRef={input}
          size="small"
          fullWidth
          value={search}
          placeholder={t("admin.search.placeholder")}
          onChange={(event) => {
            setSearch(event.target.value);
            setHighlighted(0);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          slotProps={{
            // `htmlInput` rather than `input`: the former reaches the `<input>`
            // element, the latter the wrapper MUI draws around it. A label on
            // the wrapper is not announced, and is not what a screen reader or a
            // test finds by accessible name.
            htmlInput: {
              "aria-label": t("admin.search.label"),
              role: "combobox",
              "aria-expanded": open,
              "aria-controls": "admin-page-list",
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {current === undefined ? null : t(current.labelKey)}
                  </Typography>
                  <KeyboardArrowDownIcon
                    fontSize="small"
                    color="action"
                    sx={{
                      ml: 0.5,
                      transform: open ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />

        <Popper
          open={open}
          anchorEl={anchor.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchor.current?.offsetWidth }}
        >
          <Paper
            elevation={8}
            variant="elevation"
            sx={{ mt: 0.5 }}
            id="admin-page-list"
            role="listbox"
          >
            {visible.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("admin.search.noMatches")}
                </Typography>
              </Box>
            ) : (
              groups.map((group, index) => (
                <Box key={group.id}>
                  {index > 0 ? <Divider /> : null}
                  <Typography
                    variant="caption"
                    component="p"
                    color="text.secondary"
                    sx={{ px: 2, pt: 1, textTransform: "uppercase" }}
                  >
                    {t(group.labelKey)}
                  </Typography>
                  {group.pages.map((page) => (
                    <ListItemButton
                      key={page.id}
                      role="option"
                      aria-selected={visible.indexOf(page) === highlighted}
                      selected={visible.indexOf(page) === highlighted}
                      onClick={() => {
                        go(page);
                      }}
                    >
                      <ListItemText
                        primary={t(page.labelKey)}
                        slotProps={{
                          primary: {
                            fontWeight:
                              page.id === current?.id ? 600 : undefined,
                          },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </Box>
              ))
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

/** The page a path is showing, for labelling the field. */
export const adminPageForPath = (pathname: string): AdminPage | undefined =>
  adminPages.find((page) => pathname.startsWith(page.path));
